<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid class="px-3 px-sm-6 py-4">
      <div class="d-flex flex-column flex-md-row align-start align-md-center mb-4">
        <div class="d-flex align-center">
          <h2 class="text-h6 text-sm-h5">{{ title }}</h2>
          <v-chip v-if="usingMock" small outlined color="orange" class="ml-3">mock data</v-chip>
        </div>
        <v-spacer class="d-none d-md-block" />
        <div v-if="!loading" class="d-flex align-center flex-wrap mt-2 mt-md-0">
          <template v-for="(grp, i) in identifierGroups">
            <span :key="'lbl' + i" class="text--secondary text-caption mr-1" :class="i === 0 ? 'ml-md-4' : 'ml-4'">
              {{ grp.label }}:
            </span>
            <v-chip v-for="num in grp.values" :key="grp.label + num" x-small class="mr-1 mb-1" color="primary" outlined>
              {{ num }}
            </v-chip>
          </template>
        </div>
      </div>

      <!-- Filters (factory dropdown + date window). Affect all metrics except the
           Receiving vs Processing by Factory bar at the bottom. -->
      <v-row v-if="showFilters" dense class="mb-1">
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-select
            v-model="filters.entityId"
            :items="filterOptions"
            item-text="name"
            item-value="id"
            :label="filterLabel"
            outlined
            dense
            hide-details
            clearable
            @change="reload"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="3">
          <date-range-field
            v-model="filters.dateRange"
            :label="$t('unionDashboard.dateRange')"
            :max="today"
            @change="reload"
          />
        </v-col>
      </v-row>

      <!-- Core KPI tiles -->
      <v-row dense>
        <v-col v-for="kpi in kpis" :key="kpi.label" cols="12" sm="6" md="3">
          <stat-card :label="kpi.label" :value="kpi.value" :unit="kpi.unit" :icon="kpi.icon" :loading="loading" />
        </v-col>
      </v-row>

      <!-- Operational counts -->
      <v-row dense class="mt-1">
        <v-col v-for="op in operational" :key="op.label" cols="6" sm="3" md="3">
          <stat-card :label="op.label" :value="op.value" :icon="op.icon" :loading="loading" />
        </v-col>
      </v-row>

      <!-- Quality charts -->
      <v-row dense class="mt-1">
        <v-col cols="12" md="6">
          <chart-card
            :title="$t('unionDashboard.avgQualityScore')"
            :tooltip="$t('unionDashboard.avgQualityScoreTT')"
            :loading="loading"
          >
            <gauge-chart :value="qualityValue" :max="qualityMax" :label="$t('unionDashboard.avgScore')" :height="chartHeight" />
          </chart-card>
        </v-col>
        <v-col cols="12" md="6">
          <chart-card
            :title="$t('unionDashboard.greenCoffeeGrades')"
            :tooltip="$t('unionDashboard.greenCoffeeGradesTT')"
            :loading="loading"
          >
            <donut-chart :series="gradeSeries" :labels="gradeLabels" :total-label="$t('unionDashboard.kilograms')" :height="chartHeight" />
          </chart-card>
        </v-col>
      </v-row>

      <!-- Receiving vs Processing breakdown — always full scope (NOT affected by filters),
           pinned to bottom, lazy-loaded when it scrolls into view. Tiered parent
           (test org) shows a second chart: by-union AND by-society. -->
      <v-row dense class="mt-1">
        <v-col cols="12">
          <chart-card
            ref="breakdownCard"
            :title="breakdownTitle"
            :tooltip="$t('unionDashboard.receivingVsProcessingTT')"
            :loading="breakdownCardLoading"
            :empty="breakdownEmpty"
          >
            <bar-chart
              :series="breakdownSeries"
              :categories="breakdownCategories"
              :y-title="$t('unionDashboard.kilograms')"
              :height="chartHeight"
              id="coffee-receiving-processing"
            />
          </chart-card>
        </v-col>
        <v-col v-if="hasSocietyBreakdown" cols="12">
          <chart-card
            :title="$t('unionDashboard.breakdownBySociety')"
            :tooltip="$t('unionDashboard.receivingVsProcessingTT')"
            :loading="breakdownCardLoading"
            :empty="societyBreakdownEmpty"
          >
            <bar-chart
              :series="societyBreakdownSeries"
              :categories="societyBreakdownCategories"
              :y-title="$t('unionDashboard.kilograms')"
              :height="chartHeight"
              id="coffee-receiving-processing-society"
            />
          </chart-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Breadcrumb from "@/components/core/Breadcrumb";
import StatCard from "@/components/dashboard/StatCard.vue";
import ChartCard from "@/components/dashboard/ChartCard.vue";
import BarChart from "@/components/dashboard/BarChart.vue";
import DonutChart from "@/components/dashboard/DonutChart.vue";
import GaugeChart from "@/components/dashboard/GaugeChart.vue";
import DateRangeField from "@/components/dashboard/DateRangeField.vue";
import CoffeeDashboardService, {
  MOCK_DASHBOARDS,
  resolveDashboardType,
  DASHBOARD_TYPES,
} from "@/_services/CoffeeDashboardService";

export default {
  name: "CoffeeDashboard",
  components: { breadcrumb: Breadcrumb, StatCard, ChartCard, BarChart, DonutChart, GaugeChart, DateRangeField },
  data() {
    return {
      loading: true,
      type: resolveDashboardType(),
      data: {},
      filterOptions: [],
      filters: { entityId: null, dateRange: [] },
      breakdownRequested: false,
      breakdownObserver: null,
    };
  },
  computed: {
    chartHeight() {
      return this.$vuetify.breakpoint.smAndDown ? 260 : 340;
    },
    // Parent/union/society are backed by real endpoints; factory is still mock.
    usingMock() {
      return this.type === DASHBOARD_TYPES.FACTORY;
    },
    // Filter bar only where a real scoped endpoint backs it.
    showFilters() {
      return [DASHBOARD_TYPES.PARENT, DASHBOARD_TYPES.UNION, DASHBOARD_TYPES.SOCIETY].includes(this.type);
    },
    // Parent filters by union; union filters by society; society filters by factory.
    filterLabel() {
      if (this.type === DASHBOARD_TYPES.PARENT) return this.$t("unionDashboard.union");
      if (this.type === DASHBOARD_TYPES.UNION) return this.$t("unionDashboard.society");
      return this.$t("unionDashboard.factory");
    },
    today() {
      return new Date().toISOString().slice(0, 10);
    },
    apiParams() {
      const { entityId, dateRange } = this.filters;
      const [from, to] = dateRange || [];
      const idKey =
        this.type === DASHBOARD_TYPES.PARENT
          ? "unionId"
          : this.type === DASHBOARD_TYPES.UNION
          ? "societyId"
          : "factoryId";
      return {
        ...(entityId != null && { [idKey]: entityId }),
        ...(from && to && { from, to }),
      };
    },
    title() {
      const map = {
        [DASHBOARD_TYPES.PARENT]: this.$t("unionDashboard.titleParent"),
        [DASHBOARD_TYPES.UNION]: this.$t("unionDashboard.titleUnion"),
        [DASHBOARD_TYPES.SOCIETY]: this.$t("unionDashboard.titleSociety"),
        [DASHBOARD_TYPES.FACTORY]: this.$t("unionDashboard.titleFactory"),
      };
      return map[this.type] || this.$t("unionDashboard.titleUnion");
    },
    breadcrumbs() {
      return [
        { text: this.$t("unionDashboard.eudrTitle"), disabled: true, to: "/" },
        { text: this.title, disabled: true, to: "/" },
      ];
    },
    identifierGroups() {
      const id = this.data.identifiers || {};
      const groups = [];
      if (id.unionNumbers) groups.push({ label: this.$t("unionDashboard.unionNumbers"), values: id.unionNumbers });
      if (id.societyNumbers) groups.push({ label: this.$t("unionDashboard.societyNumbers"), values: id.societyNumbers });
      if (id.factoryNumbers) groups.push({ label: this.$t("unionDashboard.factoryNumbers"), values: id.factoryNumbers });
      return groups;
    },
    kpis() {
      const c = this.data.core || {};
      const farmers = { label: this.$t("unionDashboard.totalFarmers"), value: c.totalFarmers, icon: "mdi-account-group" };
      const farms = { label: this.$t("unionDashboard.totalFarms"), value: c.totalFarms, icon: "mdi-barn" };
      // Tiered parent (test org): show receiving/processing at BOTH the society
      // (factory) tier and the union tier, since factory output feeds the union.
      if (this.data.tiered) {
        return [
          farmers,
          farms,
          { label: this.$t("unionDashboard.receivingBySociety"), value: c.receivingBySocietyKg, unit: "kg", icon: "mdi-basket-fill" },
          { label: this.$t("unionDashboard.processingBySociety"), value: c.processingBySocietyKg, unit: "kg", icon: "mdi-factory" },
          { label: this.$t("unionDashboard.receivingByUnion"), value: c.receivingByUnionKg, unit: "kg", icon: "mdi-basket-fill" },
          { label: this.$t("unionDashboard.processingByUnion"), value: c.processingByUnionKg, unit: "kg", icon: "mdi-factory" },
        ];
      }
      return [
        farmers,
        farms,
        { label: this.$t("unionDashboard.totalReceiving"), value: c.totalReceivingKg, unit: "kg", icon: "mdi-basket-fill" },
        { label: this.$t("unionDashboard.totalProcessing"), value: c.totalProcessingKg, unit: "kg", icon: "mdi-factory" },
      ];
    },
    operational() {
      const o = this.data.operational || {};
      // icon + label per known operational key; render only keys present in the scope's data.
      const meta = {
        unions: { label: this.$t("unionDashboard.unions"), icon: "mdi-office-building" },
        societies: { label: this.$t("unionDashboard.societies"), icon: "mdi-domain" },
        mills: { label: this.$t("unionDashboard.mills"), icon: "mdi-factory" },
        crops: { label: this.$t("unionDashboard.crops"), icon: "mdi-sprout" },
        bushes: { label: this.$t("unionDashboard.bushes"), icon: "mdi-tree" },
      };
      return Object.keys(meta)
        .filter((k) => o[k] != null)
        .map((k) => ({ ...meta[k], value: o[k] }));
    },
    breakdownTitle() {
      const by = this.data.breakdown?.by;
      const map = {
        union: this.$t("unionDashboard.breakdownByUnion"),
        society: this.$t("unionDashboard.breakdownBySociety"),
        factory: this.$t("unionDashboard.breakdownByFactory"),
        crop: this.$t("unionDashboard.breakdownByCrop"),
      };
      return map[by] || this.$t("unionDashboard.receivingVsProcessing");
    },
    // Parent + union lazy-load their (heavy) breakdown from a separate endpoint.
    lazyBreakdownType() {
      return this.type === DASHBOARD_TYPES.PARENT || this.type === DASHBOARD_TYPES.UNION;
    },
    // Breakdown is lazy-loaded for parent/union; show its skeleton until it arrives.
    breakdownCardLoading() {
      if (this.loading) return true;
      if (this.lazyBreakdownType) return !this.data.breakdown;
      return false;
    },
    breakdownEmpty() {
      return !!this.data.breakdown && (this.data.breakdown.entities || []).length === 0;
    },
    breakdownCategories() {
      return (this.data.breakdown?.entities || []).map((e) => e.name);
    },
    breakdownSeries() {
      const entities = this.data.breakdown?.entities || [];
      return [
        { name: this.$t("unionDashboard.receiving"), data: entities.map((e) => e.receivingKg) },
        { name: this.$t("unionDashboard.processing"), data: entities.map((e) => e.processingKg) },
      ];
    },
    // Second breakdown (tiered parent): Receiving vs Processing by Society.
    hasSocietyBreakdown() {
      return !!(this.data.breakdown && this.data.breakdown.society);
    },
    societyBreakdownEmpty() {
      return this.hasSocietyBreakdown && (this.data.breakdown.society.entities || []).length === 0;
    },
    societyBreakdownCategories() {
      return (this.data.breakdown?.society?.entities || []).map((e) => e.name);
    },
    societyBreakdownSeries() {
      const entities = this.data.breakdown?.society?.entities || [];
      return [
        { name: this.$t("unionDashboard.receiving"), data: entities.map((e) => e.receivingKg) },
        { name: this.$t("unionDashboard.processing"), data: entities.map((e) => e.processingKg) },
      ];
    },
    qualityValue() {
      return this.data.quality?.avgScore || 0;
    },
    qualityMax() {
      return this.data.quality?.maxScore || 100;
    },
    gradeLabels() {
      return (this.data.quality?.greenGrades || []).map((g) => g.grade);
    },
    gradeSeries() {
      return (this.data.quality?.greenGrades || []).map((g) => g.count);
    },
  },
  async mounted() {
    await this.load();
    this.setupBreakdownLazyLoad();
  },
  beforeDestroy() {
    if (this.breakdownObserver) this.breakdownObserver.disconnect();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const base = MOCK_DASHBOARDS[this.type] || MOCK_DASHBOARDS.union;

        if (this.type === DASHBOARD_TYPES.PARENT) {
          // Summary only (tiles + quality). Breakdown lazy-loaded separately;
          // preserve it across filter reloads since it's filter-independent.
          const res = await CoffeeDashboardService.getParentDashboard(this.apiParams);
          const payload = res.data?.data || res.data || {};
          this.data = { ...payload, breakdown: this.data && this.data.breakdown };
          this.filterOptions = payload.unions || [];
        } else if (this.type === DASHBOARD_TYPES.UNION) {
          // Summary only (tiles + quality). Breakdown is lazy-loaded separately;
          // preserve it across filter reloads since it's filter-independent.
          const res = await CoffeeDashboardService.getUnionDashboard(this.apiParams);
          const payload = res.data?.data || res.data || {};
          this.data = { ...payload, breakdown: this.data && this.data.breakdown };
          this.filterOptions = payload.societies || [];
        } else if (this.type === DASHBOARD_TYPES.SOCIETY) {
          // Real core (farmers/farms); mock supplies not-yet-built sections.
          const res = await CoffeeDashboardService.getSocietyDashboard(this.apiParams);
          const payload = res.data?.data || res.data || {};
          this.data = { ...base, core: { ...base.core, ...payload.core } };
          this.filterOptions = payload.factories || [];
        } else {
          // Factory: mock only (no admin login yet).
          this.data = { ...base };
        }
      } catch (e) {
        console.error("Coffee dashboard load failed", e);
        this.data = MOCK_DASHBOARDS[this.type] || {};
      } finally {
        this.loading = false;
      }
    },
    // Re-fetch summary on filter change. The breakdown bar is filter-independent
    // and already loaded, so it is not re-fetched here.
    reload() {
      this.load();
    },
    // Lazy-load the heavy breakdown only for union, and only when it scrolls into
    // view. Falls back to an immediate fetch where IntersectionObserver is absent.
    setupBreakdownLazyLoad() {
      if (!this.lazyBreakdownType) return;
      const el = this.$refs.breakdownCard && this.$refs.breakdownCard.$el;
      if (!el || typeof IntersectionObserver === "undefined") {
        this.loadBreakdown();
        return;
      }
      this.breakdownObserver = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.loadBreakdown();
          this.breakdownObserver.disconnect();
        }
      });
      this.breakdownObserver.observe(el);
    },
    async loadBreakdown() {
      if (this.breakdownRequested) return;
      this.breakdownRequested = true;
      const isParent = this.type === DASHBOARD_TYPES.PARENT;
      try {
        const res = isParent
          ? await CoffeeDashboardService.getParentBreakdown()
          : await CoffeeDashboardService.getUnionBreakdown();
        const payload = res.data?.data || res.data || {};
        this.$set(this.data, "breakdown", payload);
      } catch (e) {
        console.error("Coffee breakdown load failed", e);
        this.$set(this.data, "breakdown", { by: isParent ? "union" : "society", entities: [] });
      }
    },
  },
};
</script>
